import React, { useState } from 'react';

import Card from '@mui/material/Card';
import { FormControlLabel, Radio } from '@mui/material';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import DialogContent from '@mui/material/DialogContent';

import DialogActions from '@mui/material/DialogActions';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import type { Item } from 'src/components/ui/RadioGroupModal';
import RadioGroupModal from 'src/components/ui/RadioGroupModal';
import Avatar from 'src/components/ui/Avatar';
import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Typography from 'src/components/ui/Typography';
import Button from 'src/components/ui/Button';
import IconButton from 'src/components/ui/IconButton';
import Icon from 'src/components/ui/Icon';
import Tag from 'src/components/ui/Tag';
import MyPropertyCardMenuButton from 'src/components/edit-profile/MyPropertyCardMenuButton';
import Image from 'src/components/ui/Image';

import { colours } from 'src/constants/colours';
import { noImagePath } from 'src/constants/defaultImages';
import {
    routePathAd,
    routePathCreateAdStep1WithAdId,
    routePathProfileAds,
    routePathProfileCompanyShowcase,
} from 'src/constants/router';

import useAlerts from 'src/hooks/useAlerts';
import useAppRouter from 'src/hooks/useAppRouter';
import useModalOpenState from 'src/hooks/useModalOpenState';

import formatPrice from 'src/utils/formatPrice';
import isNotNull from 'src/utils/isNotNull';

import type { UserAdsQuery, Medium, CompanyAdsSearchQuery } from 'src/types/__generated__/graphql';
import { AdStatuses, ReasonWhyAdArchived, AdTypes } from 'src/types/__generated__/graphql';
import { useUpdateAdStatusMutation, useSoftDeleteAdMutation } from 'src/hooks/__generated__/queries';
import ConfirmActionModal from 'src/components/ui/ConfirmActionModal';
import SoldPropertyInfosModal from 'src/components/edit-profile/SoldPropertyInfosModal';
import { styledComponent, styledTag } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';
import useBreakpoints from 'src/hooks/useBreakpoints';
import { breakpoints } from 'src/constants/breakpoints';

const TagContainer = styledComponent(Box)`
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    z-index: 10;
    > div {
        margin-bottom: 0.125rem;
        width: 100%;
    }
`;

const StyledDialog = styledComponent(Dialog)`
    .MuiDialogContent-root {
        padding: 0 1.5rem;
    }
    .MuiDialogActions-root,
    .MuiDialogTitle-root {
        padding: 1.5rem;
    }
`;

StyledDialog.defaultProps = {
    maxWidth: 'sm',
    fullWidth: true,
};

const StyledCard = styledComponent(Card)`
    background: ${colours.black.O04};
    margin: 1rem 0;
    display: flex;
    width: 93%;
`;

StyledCard.defaultProps = {
    elevation: 0,
};

interface ImageContainerProps {
    $status?: AdStatuses | null;
}

export const ImageContainer = styledTag('div')`
    border-radius: 1rem;
    overflow:hidden;
    position: relative;
    ${(props: ImageContainerProps) =>
        props.$status !== AdStatuses.Available &&
        `
        img {
            opacity: 0.5;
        }
    `}
`;

const ImageWrapper = styledTag('div')`
    > div {
        display: block !important;
    }
`;

const StyledTag = styledComponent(Tag)`
    &:not(.MuiChip-colorPrimary):not(.MuiChip-colorSecondary) {
        color: ${colours.white.main};
        background-color: ${colours.tertiary.main};
        border: ${colours.tertiary.main};
    }
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const StyledBox = styledComponent(Box)`
    margin-left: auto;
`;

const StyledFormControlLabel = styledComponent(FormControlLabel)`
    &.MuiFormControlLabel-root {
        margin-left: 0rem;
        margin-right: 0rem;
    }
`;

const StyledCardContent = styledComponent(CardContent)`
    max-width: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 1rem 1.5rem 2rem 1.5rem;
    @media (max-width: ${breakpoints.breakPoint450 - 1}px) {
        padding: 0rem 1rem 0rem 1rem;
        &.MuiCardContent-root {
            padding-bottom: 0rem;
        }
    }
`;

const StyledCardActions = styledComponent(CardActions)<{
    $isAvatarDisplay: boolean;
    $adStatuses?: boolean;
    $isNotOnMyProfileOrMyCompanyProfile?: boolean;
}>`
    display: flex;
    flex-direction: row;
    margin-top: auto;
    padding: 0;
    ${({ $isAvatarDisplay }) =>
        $isAvatarDisplay &&
        `
    &.MuiCardActions-spacing {
        > :last-child {
            margin-left:auto;
        }
    }`}

    ${({ $isAvatarDisplay, $adStatuses }) =>
        $isAvatarDisplay &&
        $adStatuses &&
        `
    &.MuiCardActions-spacing {
        > :last-child {
            margin-left:auto;
        }
    }`}

    ${({ $isAvatarDisplay, $isNotOnMyProfileOrMyCompanyProfile }) =>
        $isAvatarDisplay &&
        $isNotOnMyProfileOrMyCompanyProfile &&
        `
        &.MuiCardActions-spacing {
            > :last-child {
                margin-left:auto;
            }
        }`}
`;

interface ConfirmModalProps {
    type: string;
    title: string;
    description: string;
    confirm: string;
}

const confirmModalPropsOptions = {
    ARCHIVE: {
        type: 'ARCHIVE',
        title: 'edit-profile:archiveAd',
        description: 'edit-profile:archiveAdConfirmContent',
        confirm: 'common:confirm',
    },
    SOLD: {
        type: 'SOLD',
        title: 'edit-profile:soldAdConfirmTitle',
        description: 'edit-profile:soldAdConfirmDescription',
        confirm: 'common:confirm',
    },
    RENTED: {
        type: 'RENTED',
        title: 'edit-profile:rentedAdConfirmTitle',
        description: 'edit-profile:rentedAdConfirmDescription',
        confirm: 'common:confirm',
    },
};

const archiveReasonsOptions = [
    ReasonWhyAdArchived.ExpiredMandate,
    ReasonWhyAdArchived.WithdrawnFromSaleRent,
    ReasonWhyAdArchived.SoldRentedByColleague,
    ReasonWhyAdArchived.SoldRented,
];

export type CompanyAd = ArrayElement<NonNullable<CompanyAdsSearchQuery['ads']>['items']>;
export type UserAd = ArrayElement<UserAdsQuery['userAds']>;
type Ad = CompanyAd | UserAd;
interface Props<T = Ad> {
    ad: T;
    isSelectingAd?: boolean;
    onSelection?: (selectedAdId: string) => void;
    isSelectedAd?: boolean;
}

const MyPropertyCard: React.FC<Props> = <T extends Ad>({ ad, isSelectingAd, onSelection, isSelectedAd }: Props<T>) => {
    const { t, i18n } = useTranslation(['ad', 'edit-profile', 'common']);
    const { addAlert } = useAlerts();
    const router = useAppRouter();
    const { logEventDynamic } = useDynamicLogEvents();

    const [priceSold, setPriceSold] = useState<number | undefined>(undefined);

    const [updateAdStatus] = useUpdateAdStatusMutation();
    const [softDeleteAd] = useSoftDeleteAdMutation();

    const { props: restoreModalProps, open: openRestoreModal } = useModalOpenState();
    const { props: deleteModalProps } = useModalOpenState();
    const { props: soldInfosModalProps, open: openSoldInfosModal } = useModalOpenState();
    const { props: archiveAdReasonsModalProps, open: openArchiveAdReasonsModal } = useModalOpenState();
    const { props: confirmActionModalProps, open: openConfirmModal } = useModalOpenState();

    const [confirmModalProps, setConfirmModalProps] = useState<ConfirmModalProps>(confirmModalPropsOptions.ARCHIVE);
    const [selectedReason, setSelectedReason] = useState<ReasonWhyAdArchived | undefined>(undefined);

    const { isMobile, isSmallMobile } = useBreakpoints();

    const reasonItems: Item[] = archiveReasonsOptions.map((reason) => ({
        value: reason,
        label: t(`ad:archiveReasons.${ad.type}.${reason}`),
        dataTarget: 'archive-ad-reason-radio',
        dataTargetId: `archive-ad-reason-${reason.replace('_', '-')}`,
    }));

    const refetchQueries = router.asPath.includes(routePathProfileAds)
        ? ['UserAdsSearch', 'CurrentUserProfile']
        : ['CompanyAdsSearch', 'CompanyProfile'];

    const handleRestore = async () => {
        await updateAdStatus({
            variables: {
                id: ad.id,
                status: AdStatuses.Available,
            },
            refetchQueries,
            awaitRefetchQueries: true,
        });

        restoreModalProps.onClose();
        addAlert({
            type: 'success',
            message: t('edit-profile:restoreSuccess'),
        });
    };

    const handleDelete = async () => {
        await softDeleteAd({
            variables: {
                id: ad.id,
            },
            refetchQueries,
            awaitRefetchQueries: true,
        });

        deleteModalProps.onClose();
        addAlert({
            type: 'success',
            message: t('edit-profile:deleteAdSuccess'),
        });
    };

    const handleSoldOrRented = () => {
        if (ad.type === AdTypes.Sell) {
            logEventDynamic(AnalyticsEvent.WasSold);
            openSoldInfosModal();
            setConfirmModalProps(confirmModalPropsOptions.SOLD);
        } else {
            logEventDynamic(AnalyticsEvent.WasRented);
            setConfirmModalProps(confirmModalPropsOptions.RENTED);
            openConfirmModal();
        }
    };

    const handleCloseOrCancelInfosModal = () => {
        setPriceSold(undefined);
        soldInfosModalProps.onClose();
    };

    const handleConfirmOrSkipInfosModal = (price: number | undefined) => {
        soldInfosModalProps.onClose();
        setPriceSold(price);
        setConfirmModalProps(confirmModalPropsOptions.SOLD);
        openConfirmModal();
    };

    const handleArchiveAd = async () => {
        await updateAdStatus({
            variables: {
                id: ad.id,
                status: AdStatuses.Archived,
                reasonWhyAdArchived: selectedReason,
            },
            refetchQueries,
            awaitRefetchQueries: true,
        });

        addAlert({
            type: 'success',
            message: t('edit-profile:archiveAdSuccess'),
        });
    };

    const handleEndAd = async () => {
        await updateAdStatus({
            variables: {
                id: ad.id,
                status: AdStatuses.Ended,
                priceSold,
            },
            refetchQueries,
            awaitRefetchQueries: true,
        });
    };

    const handleConfirmConfirmActionModal = async () => {
        if (confirmModalProps.type === confirmModalPropsOptions.ARCHIVE.type) {
            await handleArchiveAd();
        } else {
            await handleEndAd();
        }
    };

    const handleCloseOrCancelReasonsModal = () => {
        setSelectedReason(undefined);
        archiveAdReasonsModalProps.onClose();
    };

    const handleConfirmReasonsModal = (value: ReasonWhyAdArchived) => {
        setSelectedReason(value);
        archiveAdReasonsModalProps.onClose();

        if (value === ReasonWhyAdArchived.SoldRented) {
            if (ad.type === AdTypes.Sell) {
                openSoldInfosModal();
            } else {
                setConfirmModalProps(confirmModalPropsOptions.RENTED);
                openConfirmModal();
            }
        } else {
            setConfirmModalProps(confirmModalPropsOptions.ARCHIVE);
            openConfirmModal();
        }
    };

    const handleSkipReasonsModal = () => {
        archiveAdReasonsModalProps.onClose();
        setSelectedReason(undefined);
        setConfirmModalProps(confirmModalPropsOptions.ARCHIVE);
        openConfirmModal();
    };

    const priceString =
        ad.type === 'sell'
            ? formatPrice({ price: Number(ad.price), lang: i18n.language })
            : t('ad:rentPrice', {
                  rentPrice: formatPrice({ price: Number(ad.rent), lang: i18n.language }),
              });

    const mainImage = String(ad.media[0]?.url ?? noImagePath);

    const isOnCompanyProfileShowcase = router.asPath.includes(routePathProfileCompanyShowcase);
    const isOnUserProfileShowcase = router.asPath.includes(routePathProfileAds);

    return (
        <>
            <StyledCard>
                <ImageContainer $status={ad.status}>
                    {mainImage && (
                        <ImageWrapper>
                            <Image
                                src={mainImage}
                                alt="my-property-image"
                                width={192}
                                height={192}
                                style={{ objectFit: 'cover' }}
                            />
                        </ImageWrapper>
                    )}
                    {ad.status === AdStatuses.Available && (
                        <TagContainer>
                            <Tag
                                color="primary"
                                size="small"
                                data-target="my-property-card"
                                data-target-id="my-property-card-type-tag"
                                label={t(`edit-profile:${ad.type}`)}
                                icon={
                                    <Icon
                                        data-target="my-property-card"
                                        size="1rem"
                                        name={ad.type === AdTypes.Sell ? 'ForSaleOutlined' : 'HouseKeyOutlined'}
                                    />
                                }
                            />
                        </TagContainer>
                    )}
                    {ad.status === AdStatuses.Ended && (
                        <StyledTag
                            label={t(`edit-profile:${ad.type === 'sell' ? 'soldLabel' : 'rentedLabel'}`)}
                            size="small"
                        />
                    )}
                </ImageContainer>
                <StyledCardContent>
                    <Box display="flex" alignItems="center">
                        <Typography
                            data-target="my-property-card"
                            data-target-id="my-property-card-price"
                            variant={isSmallMobile ? 'h3' : 'h2'}
                        >
                            {priceString}
                        </Typography>
                        {isSelectingAd ? (
                            <StyledBox onClick={() => onSelection?.(ad.id)}>
                                <StyledFormControlLabel
                                    name="adId"
                                    checked={isSelectedAd}
                                    value={ad.id}
                                    label=""
                                    control={<Radio checkedIcon={<Icon name="CheckMarkBlue" />} />}
                                />
                            </StyledBox>
                        ) : (
                            <Box ml="auto" display="flex" alignItems="center">
                                <Box mr={1} display="flex" alignItems="center">
                                    <Icon color={colours.backgroundGray} size="1rem" name="Show" />
                                    <Box ml={0.5} mt={-0.25}>
                                        <Typography
                                            variant="subtitle2"
                                            data-target="my-property-card"
                                            data-target-id="my-property-card-views"
                                        >
                                            <small>{ad.stats.viewCount}</small>
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box display="flex" alignItems="center">
                                    <Icon color={colours.backgroundGray} size="1rem" name="BookmarkGrey" />
                                    <Box ml={0.5} mt={-0.25}>
                                        <Typography
                                            variant="subtitle2"
                                            data-target="my-property-card"
                                            data-target-id="my-property-card-favorites"
                                        >
                                            <small>{ad.stats.favoriteCount}</small>
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                    <Box my={0.5} display="flex" alignItems="center">
                        {isNotNull(ad.bedroomNumber) && (
                            <Box mr={1} display="flex" alignItems="center">
                                <Icon color={colours.backgroundGray} size="1rem" name="BedRoom" />
                                <Box ml={0.5}>
                                    <Typography
                                        variant="body2"
                                        data-target="my-property-card"
                                        data-target-id="my-property-card-bedrooms"
                                    >
                                        {t('ad:countBedRoomsShort', {
                                            count: ad.bedroomNumber,
                                        })}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        {isNotNull(ad.roomNumber) && (
                            <Box mr={1} display="flex" alignItems="center">
                                <Icon color={colours.backgroundGray} size="1rem" name="Room" />
                                <Box ml={0.5}>
                                    <Typography
                                        variant="body2"
                                        data-target="my-property-card"
                                        data-target-id="my-property-card-rooms"
                                    >
                                        {t('ad:countRoomsShort', {
                                            count: ad.roomNumber,
                                        })}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        {isNotNull(ad.livingArea) && (
                            <Box mr={1} display="flex" alignItems="center">
                                <Icon color={colours.backgroundGray} size="1em" name="HomeArea" />
                                <Box ml={0.5}>
                                    <Typography
                                        variant="body2"
                                        data-target="my-property-card"
                                        data-target-id="my-property-card-living-area"
                                        dangerouslySetInnerHTML={{
                                            __html: t('ad:livingArea', {
                                                livingArea: ad.livingArea,
                                            }),
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}
                        {isNotNull(ad.surfaceArea) && (
                            <Box mr={1} display="flex" alignItems="center">
                                <Icon color={colours.backgroundGray} size="1rem" name="Area" />
                                <Box ml={0.5}>
                                    <Typography
                                        variant="body2"
                                        data-target="my-property-card"
                                        data-target-id="my-property-card-surface-area"
                                    >
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: t('ad:surfaceArea', {
                                                    surfaceArea: ad.surfaceArea,
                                                }),
                                            }}
                                        />
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        data-target="my-property-card"
                        data-target-id="my-property-card-address"
                    >
                        {`${ad.address.cityName}${ad.address.zipCode ? `, ${ad.address.zipCode}` : ''}`}
                    </Typography>
                    <StyledCardActions
                        $isAvatarDisplay={Boolean(ad.assignedTo)}
                        $adStatuses={!(ad.status === AdStatuses.Available)}
                        $isNotOnMyProfileOrMyCompanyProfile={!isOnCompanyProfileShowcase && !isOnUserProfileShowcase}
                    >
                        {isMobile && (
                            <IconButton
                                data-target="my-property-card"
                                data-target-id="my-property-card-see-button"
                                color="inherit"
                                iconProps={{
                                    name: 'Eye',
                                    size: isSmallMobile ? '0.75rem' : '1rem',
                                }}
                                onClick={async () => {
                                    logEventDynamic(AnalyticsEvent.SeeAd);
                                    await router.push(`${routePathAd}/${ad.id}`);
                                }}
                            />
                        )}
                        {!isMobile && (
                            <Button
                                data-target="my-property-card"
                                data-target-id="my-property-card-see-button"
                                variant="outlined"
                                color="white"
                                startIcon={<Icon name="Eye" />}
                                onClick={async () => {
                                    logEventDynamic(AnalyticsEvent.SeeAd);
                                    await router.push(`${routePathAd}/${ad.id}`);
                                }}
                            >
                                {t('common:see')}
                            </Button>
                        )}
                        {(isOnCompanyProfileShowcase || isOnUserProfileShowcase) &&
                            (ad.status === AdStatuses.Available ? (
                                <>
                                    {!isMobile && (
                                        <Button
                                            data-target="my-property-card"
                                            data-target-id="my-property-card-edit-button"
                                            variant="outlined"
                                            color="white"
                                            startIcon={<Icon name="Edit" />}
                                            onClick={async () => {
                                                logEventDynamic(AnalyticsEvent.EditAd);
                                                await router.push(`${routePathCreateAdStep1WithAdId}${ad.id}`);
                                            }}
                                        >
                                            {t('edit-profile:edit')}
                                        </Button>
                                    )}
                                    {isMobile && (
                                        <IconButton
                                            data-target="my-property-card"
                                            data-target-id="my-property-card-edit-button"
                                            color="inherit"
                                            iconProps={{
                                                name: 'Edit',
                                                size: isSmallMobile ? '0.75rem' : '1rem',
                                            }}
                                            onClick={async () => {
                                                logEventDynamic(AnalyticsEvent.EditAd);
                                                await router.push(`${routePathCreateAdStep1WithAdId}${ad.id}`);
                                            }}
                                        />
                                    )}
                                    <Box>
                                        <MyPropertyCardMenuButton
                                            ad={ad}
                                            onSoldOrRentedClick={handleSoldOrRented}
                                            onArchiveClick={openArchiveAdReasonsModal}
                                            isSelectingAd={isSelectingAd}
                                        />
                                    </Box>
                                </>
                            ) : (
                                <>
                                    {ad.status === AdStatuses.Ended && (
                                        <>
                                            {!isMobile && (
                                                <Box ml="0.5rem">
                                                    <Button
                                                        data-target="my-property-card"
                                                        data-target-id="my-property-card-restore-button"
                                                        variant="outlined"
                                                        color="white"
                                                        startIcon={<Icon name="CheckMarkCircleThin" />}
                                                        onClick={openRestoreModal}
                                                    >
                                                        {t('edit-profile:restore')}
                                                    </Button>
                                                </Box>
                                            )}
                                            {isMobile && (
                                                <IconButton
                                                    color="inherit"
                                                    iconProps={{
                                                        name: 'CheckMarkCircleThin',
                                                        size: isSmallMobile ? '0.75rem' : '1rem',
                                                    }}
                                                    onClick={openRestoreModal}
                                                />
                                            )}

                                            {!isMobile && (
                                                <Box ml="0.5rem">
                                                    <Button
                                                        data-target="my-property-card"
                                                        data-target-id="my-property-card-archive-button"
                                                        variant="outlined"
                                                        color="white"
                                                        startIcon={<Icon name="FileBox" />}
                                                        onClick={openArchiveAdReasonsModal}
                                                    >
                                                        {t('edit-profile:archive')}
                                                    </Button>
                                                </Box>
                                            )}
                                            {isMobile && (
                                                <IconButton
                                                    color="inherit"
                                                    iconProps={{
                                                        name: 'FileBox',
                                                        size: isSmallMobile ? '0.75rem' : '1rem',
                                                    }}
                                                    onClick={openArchiveAdReasonsModal}
                                                />
                                            )}
                                        </>
                                    )}

                                    {ad.status === AdStatuses.Archived && (
                                        <MyPropertyCardMenuButton
                                            ad={ad}
                                            onSoldOrRentedClick={handleSoldOrRented}
                                            onArchiveClick={openArchiveAdReasonsModal}
                                            onRestoreClick={openRestoreModal}
                                            isSelectingAd={isSelectingAd}
                                        />
                                    )}
                                </>
                            ))}
                        {ad.assignedTo && (
                            <Box ml={4}>
                                <Avatar
                                    image={ad.assignedTo.image as Medium}
                                    alt={ad.assignedTo.fullName as string}
                                    fallbackText={ad.assignedTo.initials as string}
                                    size={40}
                                />
                            </Box>
                        )}
                    </StyledCardActions>
                </StyledCardContent>
            </StyledCard>

            <StyledDialog open={restoreModalProps.open} onClose={restoreModalProps.onClose}>
                <DialogTitle onClose={restoreModalProps.onClose}>
                    <Typography variant="h2">{t(`edit-profile:restoreConfirmTitle`)}</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography color="dark">{t(`edit-profile:restoreConfirmContent`)}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={restoreModalProps.onClose}>
                        {t('common:modal.cancel')}
                    </Button>
                    <Button onClick={handleRestore}>{t('edit-profile:restoreConfirmSubmit')}</Button>
                </DialogActions>
            </StyledDialog>

            <StyledDialog open={deleteModalProps.open} onClose={deleteModalProps.onClose}>
                <DialogTitle onClose={deleteModalProps.onClose}>
                    <Typography variant="h2">{t(`edit-profile:deleteAd`)}</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography color="dark">{t(`edit-profile:deleteAdConfirmContent`)}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={deleteModalProps.onClose}>
                        {t('common:cancel')}
                    </Button>
                    <Button onClick={handleDelete}>{t('edit-profile:delete')}</Button>
                </DialogActions>
            </StyledDialog>

            <SoldPropertyInfosModal
                open={soldInfosModalProps.open}
                onCloseOrCancel={handleCloseOrCancelInfosModal}
                onSkip={() => handleConfirmOrSkipInfosModal(undefined)}
                onConfirm={handleConfirmOrSkipInfosModal}
            />

            <RadioGroupModal
                modalDataTarget="archive-ad-reason"
                title={t('edit-profile:archiveAd')}
                description={t('edit-profile:archiveAdReasonsDescription')}
                open={archiveAdReasonsModalProps.open}
                items={reasonItems}
                onClose={handleCloseOrCancelReasonsModal}
                onCancel={handleCloseOrCancelReasonsModal}
                onSkip={handleSkipReasonsModal}
                onConfirm={(value) => handleConfirmReasonsModal(value as ReasonWhyAdArchived)}
                showCancel
                showSkip
            />

            <ConfirmActionModal
                open={confirmActionModalProps.open}
                title={t(confirmModalProps.title)}
                description={t(confirmModalProps.description)}
                confirm={t(confirmModalProps.confirm)}
                onConfirm={async () => {
                    confirmActionModalProps.onClose();
                    await handleConfirmConfirmActionModal();
                }}
                onCloseOrCancel={() => confirmActionModalProps.onClose()}
            />
        </>
    );
};

export default MyPropertyCard;
